import { IconName } from "@/components-v2/iconMapping";
import { TMessageKey } from "@/i18n/types.gen";
import { compact, omit, trim } from "lodash";
import { today, toLocalDateISOString } from "@/utils/Dates";

export type AutotextCategory =
  | "general"
  | "individualContributions"
  | "individualHoldings"
  | "shareCapitalIncrease"
  | "reductionOfShareCapital"
  | "incorporation"
  | "merger"
  | "options";

export const autotextCategories = ({
  includeShareCapitalIncrease,
  includeReductionOfShareCapital,
  includeIncorporation,
  includeCommittedCapital,
  includeMerger,
  includeOptions,
}: {
  includeCommittedCapital?: boolean;
  includeShareCapitalIncrease?: boolean;
  includeReductionOfShareCapital?: boolean;
  includeIncorporation?: boolean;
  includeMerger?: boolean;
  includeOptions?: boolean;
}): [AutotextCategory, DataRequestType[]][] =>
  compact([
    [
      "general",
      [
        "companyName",
        "companyOrgNo",
        "fairValuePerShare",
        "nominalValuePerShare",
        "numberOfShareholders",
        "recipientName",
        "taxAssessmentPerShare",
        "totalNumberOfShares",
        "totalShareCapital",
      ],
    ],
    [
      "individualHoldings",
      [
        "shareholderLoanAmount",
        "shareholderLockUpEndDate",
        "shareholderName",
        "shareholderBic",
        "shareholderIban",
        "shareholderNetAssetValuePerShare",
        "shareholderNumberOfPledgedShares",
        "shareholderShareClassesOwned",
        "shareholderTotalCostPrice",
        "shareholderTotalNumberOfShares",
        "shareholderTotalNumberOfSharesPerClass",
        "shareholderTotalNetAssetValue",
        "shareholderNetAssetValuePerShareClass",
        ...compact([
          includeCommittedCapital && "shareholderCommittedTotal",
          includeCommittedCapital && "shareholderCommittedDrawn",
          includeCommittedCapital && "shareholderCommittedDistributed",
          includeCommittedCapital && "shareholderCommittedRemaining",
        ]),
      ],
    ],
    includeShareCapitalIncrease && [
      "shareCapitalIncrease",
      [
        "boardStatementPursuantSection10_2",
        "contributionsInKindBoardStatement",
        "contributionsInKindSummary",
        "conversionOfDebtBoardStatement",
        "conversionOfDebtSetOffDeclarationStatement",
        "conversionOfDebtSetOffDeclarationSummary",
        "conversionOfDebtSummary",
        "shareCapitalIncreaseMethod",
        "shareCapitalIncreasePostRoundCommitted",
        "shareCapitalIncreaseShareClasses",
        "shareCapitalIncreaseSubscriptionPrice",
        "shareCapitalIncreaseSummary",
        "shareCapitalIncreaseTotalAmount",
        "shareCapitalIncreaseTotalContribution",
        "shareCapitalIncreaseTotalNewShares",
      ],
    ],
    includeShareCapitalIncrease && [
      "individualContributions",
      [
        "subscriberPostRoundCommitted",
        "subscriberPostRoundTotalShares",
        "subscriberTotalAmount",
        "subscriberTotalNewShares",
      ],
    ],
    includeReductionOfShareCapital && ["reductionOfShareCapital", ["boardStatementCreditorNotice"]],
    includeIncorporation && [
      "incorporation",
      [
        "incorporationTitle",
        "incorporationFounders",
        "incorporationShareCapital",
        "incorporationFees",
        "incorporationArticles",
        "incorporationBoard",
        "incorporationAuditor",
        "incorporationSignedBy",
      ],
    ],
    includeMerger && [
      "merger",
      [
        "mergerAccountingEffectiveDate",
        "mergerAcquiringCompanyAddress",
        "mergerAcquiringCompanyCity",
        "mergerAcquiringCompanyName",
        "mergerAcquiringCompanyOrgNo",
        "mergerFinancialInstrumentsDescription",
        "mergerParentName",
        "mergerParentOrgNo",
        "mergerPlanDate",
        "mergerSpecialRightsDescription",
        "mergerTransferringCompanyAddress",
        "mergerTransferringCompanyCity",
        "mergerTransferringCompanyName",
        "mergerTransferringCompanyOrgNo",
      ],
    ],
    includeOptions && [
      "options",
      [
        "optionsExpirationDate",
        "optionsGrantDate",
        "optionsNumberOfOptions",
        "optionsProgramTitle",
        "optionsRemarks",
        "optionsShareClass",
        "optionsStrikePrice",
        "optionsVestingCliff",
        "optionsVestingDuration",
        "optionsVestingFrequency",
        "optionsVestingType",
      ],
    ],
  ]);

export const autotextCategoryIcons: Record<AutotextCategory, IconName> = {
  general: "buildings",
  individualContributions: "user",
  individualHoldings: "user",
  shareCapitalIncrease: "chart-line-up",
  reductionOfShareCapital: "chart-line-down",
  incorporation: "buildings",
  merger: "arrows-in-simple",
  options: "chart-donut",
};

export const autotextCategoryLabels: Record<AutotextCategory, TMessageKey> = {
  general: "ai.autotext.category.general",
  individualContributions: "ai.autotext.category.individualContributions",
  individualHoldings: "ai.autotext.category.individualHoldings",
  shareCapitalIncrease: "ai.autotext.category.shareCapitalIncrease",
  reductionOfShareCapital: "ai.autotext.category.reductionOfShareCapital",
  incorporation: "ai.autotext.category.incorporation",
  merger: "ai.autotext.category.merger",
  options: "ai.autotext.category.options",
};

export const dataRequestTypeLabels: Record<DataRequestType, TMessageKey> = {
  boardStatementPursuantSection10_2: "ai.autotext.boardStatementPursuantSection10_2",
  companyName: "ai.autotext.companyName",
  companyOrgNo: "ai.autotext.companyOrgNo",
  contributionsInKindBoardStatement: "ai.autotext.contributionsInKindBoardStatement",
  contributionsInKindSummary: "ai.autotext.contributionsInKindSummary",
  conversionOfDebtBoardStatement: "ai.autotext.conversionOfDebtBoardStatement",
  conversionOfDebtSetOffDeclarationStatement:
    "ai.autotext.conversionOfDebtSetOffDeclarationStatement",
  conversionOfDebtSetOffDeclarationSummary: "ai.autotext.conversionOfDebtSetOffDeclarationSummary",
  conversionOfDebtSummary: "ai.autotext.conversionOfDebtSummary",
  fairValuePerShare: "ai.autotext.fairValuePerShare",
  nominalValuePerShare: "ai.autotext.nominalValuePerShare",
  numberOfShareholders: "ai.autotext.numberOfShareholders",
  recipientName: "ai.autotext.recipientName",
  shareCapitalIncreaseMethod: "ai.autotext.shareCapitalIncreaseMethod",
  shareCapitalIncreasePostRoundCommitted: "ai.autotext.shareCapitalIncreasePostRoundCommitted",
  shareCapitalIncreaseShareClasses: "ai.autotext.shareCapitalIncreaseShareClasses",
  shareCapitalIncreaseSubscriptionPrice: "ai.autotext.shareCapitalIncreaseSubscriptionPrice",
  shareCapitalIncreaseSummary: "ai.autotext.shareCapitalIncreaseSummary",
  shareCapitalIncreaseTotalAmount: "ai.autotext.shareCapitalIncreaseTotalAmount",
  shareCapitalIncreaseTotalContribution: "ai.autotext.shareCapitalIncreaseTotalContribution",
  shareCapitalIncreaseTotalNewShares: "ai.autotext.shareCapitalIncreaseTotalNewShares",
  shareholderCommittedDistributed: "ai.autotext.shareholderCommittedDistributed",
  shareholderCommittedDrawn: "ai.autotext.shareholderCommittedDrawn",
  shareholderCommittedRemaining: "ai.autotext.shareholderCommittedRemaining",
  shareholderCommittedTotal: "ai.autotext.shareholderCommittedTotal",
  shareholderLoanAmount: "ai.autotext.shareholderLoanAmount",
  shareholderLockUpEndDate: "ai.autotext.shareholderLockUpEndDate",
  shareholderName: "ai.autotext.shareholderName",
  shareholderNetAssetValuePerShare: "ai.autotext.shareholderNetAssetValuePerShare",
  shareholderNumberOfPledgedShares: "ai.autotext.shareholderNumberOfPledgedShares",
  shareholderShareClassesOwned: "ai.autotext.shareholderShareClassesOwned",
  shareholderTotalCostPrice: "ai.autotext.shareholderTotalCostPrice",
  shareholderTotalNetAssetValue: "ai.autotext.shareholderTotalNetAssetValue",
  shareholderTotalNumberOfShares: "ai.autotext.shareholderTotalNumberOfShares",
  shareholderTotalNumberOfSharesPerClass: "ai.autotext.shareholderTotalNumberOfSharesPerClass",
  shareholderNetAssetValuePerShareClass: "ai.autotext.shareholderNetAssetValuePerShareClass",
  shareholderBic: "common.bic",
  shareholderIban: "ai.autotext.shareholderIban",
  subscriberPostRoundCommitted: "ai.autotext.subscriberPostRoundCommitted",
  subscriberPostRoundTotalShares: "ai.autotext.subscriberPostRoundTotalShares",
  subscriberTotalAmount: "ai.autotext.subscriberTotalAmount",
  subscriberTotalNewShares: "ai.autotext.subscriberTotalNewShares",
  taxAssessmentPerShare: "ai.autotext.taxAssessmentPerShare",
  totalNumberOfShares: "ai.autotext.totalNumberOfShares",
  totalShareCapital: "ai.autotext.totalShareCapital",
  boardStatementCreditorNotice: "ai.autotext.boardStatementCreditorNotice",
  incorporationTitle: "ai.autotext.incorporationTitle",
  incorporationFounders: "ai.autotext.incorporationFounders",
  incorporationShareCapital: "ai.autotext.incorporationShareCapital",
  incorporationFees: "ai.autotext.incorporationFees",
  incorporationArticles: "ai.autotext.incorporationArticles",
  incorporationBoard: "ai.autotext.incorporationBoard",
  incorporationAuditor: "ai.autotext.incorporationAuditor",
  incorporationSignedBy: "ai.autotext.incorporationSignedBy",
  optionsExpirationDate: "ai.autotext.optionsExpirationDate",
  optionsGrantDate: "ai.autotext.optionsGrantDate",
  optionsNumberOfOptions: "ai.autotext.optionsNumberOfOptions",
  optionsProgramTitle: "ai.autotext.optionsProgramTitle",
  optionsRemarks: "ai.autotext.optionsRemarks",
  optionsShareClass: "ai.autotext.optionsShareClass",
  optionsStrikePrice: "ai.autotext.optionsStrikePrice",
  optionsVestingCliff: "ai.autotext.optionsVestingCliff",
  optionsVestingDuration: "ai.autotext.optionsVestingDuration",
  optionsVestingFrequency: "ai.autotext.optionsVestingFrequency",
  optionsVestingType: "ai.autotext.optionsVestingType",
  mergerPlanDate: "ai.autotext.mergerPlanDate",
  mergerAccountingEffectiveDate: "ai.autotext.mergerAccountingEffectiveDate",
  mergerAcquiringCompanyName: "ai.autotext.mergerAcquiringCompanyName",
  mergerAcquiringCompanyOrgNo: "ai.autotext.mergerAcquiringCompanyOrgNo",
  mergerAcquiringCompanyAddress: "ai.autotext.mergerAcquiringCompanyAddress",
  mergerAcquiringCompanyCity: "ai.autotext.mergerAcquiringCompanyCity",
  mergerTransferringCompanyName: "ai.autotext.mergerTransferringCompanyName",
  mergerTransferringCompanyOrgNo: "ai.autotext.mergerTransferringCompanyOrgNo",
  mergerTransferringCompanyAddress: "ai.autotext.mergerTransferringCompanyAddress",
  mergerTransferringCompanyCity: "ai.autotext.mergerTransferringCompanyCity",
  mergerParentName: "ai.autotext.mergerParentName",
  mergerParentOrgNo: "ai.autotext.mergerParentOrgNo",
  mergerFinancialInstrumentsDescription: "ai.autotext.mergerFinancialInstrumentsDescription",
  mergerSpecialRightsDescription: "ai.autotext.mergerSpecialRightsDescription",
};

export const dataRequestTypes: DataRequestType[] = Object.keys(
  dataRequestTypeLabels,
) as DataRequestType[];

export type SelectionAwareDataSource = {
  includeShareCapitalIncrease: boolean | undefined;
  includeCommittedCapital: boolean | undefined;
  includeReductionOfShareCapital: boolean | undefined;
  includeIncorporation: boolean | undefined;
  includeMerger: boolean | undefined;
  includeOptions: boolean | undefined;
};

export type CompanyName = {
  type: "companyName";
  perDate?: string;
};

export type CompanyOrgNo = {
  type: "companyOrgNo";
};

export type TaxAssessmentPerShareCompanyInformation = {
  type: "taxAssessmentPerShare";
  perDate?: string;
};

export type NominalValuePerShare = {
  type: "nominalValuePerShare";
  perDate?: string;
};

export type FairValuePerShare = {
  type: "fairValuePerShare";
  perDate?: string;
};

export type TotalShareCapital = {
  type: "totalShareCapital";
  perDate?: string;
};

export type TotalNumberOfShares = {
  type: "totalNumberOfShares";
  perDate?: string;
};

export type NumberOfShareholders = {
  type: "numberOfShareholders";
  perDate?: string;
};

export type ShareCapitalIncreaseMethod = {
  type: "shareCapitalIncreaseMethod";
};

export type ShareCapitalIncreaseTotalAmount = {
  type: "shareCapitalIncreaseTotalAmount";
};

export type ShareCapitalIncreaseSubscriptionPrice = {
  type: "shareCapitalIncreaseSubscriptionPrice";
};

export type ShareCapitalIncreaseTotalNewShares = {
  type: "shareCapitalIncreaseTotalNewShares";
};

export type ShareCapitalIncreaseShareClasses = {
  type: "shareCapitalIncreaseShareClasses";
};

export type ShareCapitalIncreaseTotalContribution = {
  type: "shareCapitalIncreaseTotalContribution";
};

export type ShareCapitalIncreasePostRoundCommitted = {
  type: "shareCapitalIncreasePostRoundCommitted";
};

export type ShareCapitalIncreaseSummary = {
  type: "shareCapitalIncreaseSummary";
};

export type ContributionsInKindSummary = {
  type: "contributionsInKindSummary";
};

export type ConversionOfDebtSummary = {
  type: "conversionOfDebtSummary";
};

export type ConversionOfDebtBoardStatement = {
  type: "conversionOfDebtBoardStatement";
};

export type ContributionsInKindBoardStatement = {
  type: "contributionsInKindBoardStatement";
};

export type ConversionOfDebtSetOffDeclarationStatement = {
  type: "conversionOfDebtSetOffDeclarationStatement";
};

export type ConversionOfDebtSetOffDeclarationSummary = {
  type: "conversionOfDebtSetOffDeclarationSummary";
};

export type BoardStatementPursuantSection10_2 = {
  type: "boardStatementPursuantSection10_2";
};

export const taxAssessmentPerShare: TaxAssessmentPerShareCompanyInformation = {
  type: "taxAssessmentPerShare",
};

export type ShareholderName = {
  type: "shareholderName";
};

export type RecipientName = {
  type: "recipientName";
};

export type ShareholderTotalNumberOfShares = {
  type: "shareholderTotalNumberOfShares";
  perDate?: string;
};

export type ShareholderTotalCostPrice = {
  type: "shareholderTotalCostPrice";
  perDate?: string;
};

export type ShareholderLockUpEndDate = {
  type: "shareholderLockUpEndDate";
};

export type ShareholderLoanAmount = {
  type: "shareholderLoanAmount";
};

export type ShareholderCommittedTotal = {
  type: "shareholderCommittedTotal";
  perDate?: string;
};

export type ShareholderCommittedDrawn = {
  type: "shareholderCommittedDrawn";
  perDate?: string;
};

export type ShareholderCommittedDistributed = {
  type: "shareholderCommittedDistributed";
  perDate?: string;
};

export type ShareholderCommittedRemaining = {
  type: "shareholderCommittedRemaining";
  perDate?: string;
};

export type ShareholderNumberOfPledgedShares = {
  type: "shareholderNumberOfPledgedShares";
};

export type ShareholderNetAssetValuePerShare = {
  type: "shareholderNetAssetValuePerShare";
  perDate?: string;
};

export type ShareholderTotalNetAssetValue = {
  type: "shareholderTotalNetAssetValue";
  perDate?: string;
};

export type ShareholderShareClassesOwned = {
  type: "shareholderShareClassesOwned";
  perDate?: string;
};

export type ShareholderTotalNumberOfSharesPerClass = {
  type: "shareholderTotalNumberOfSharesPerClass";
  perDate?: string;
};

export type ShareholderNetAssetValuePerShareClass = {
  type: "shareholderNetAssetValuePerShareClass";
  perDate?: string;
};

export type ShareholderBic = {
  type: "shareholderBic";
};

export type ShareholderIban = {
  type: "shareholderIban";
};

export type SubscriberTotalAmount = {
  type: "subscriberTotalAmount";
};

export type SubscriberTotalNewShares = {
  type: "subscriberTotalNewShares";
};

export type SubscriberPostRoundTotalShares = {
  type: "subscriberPostRoundTotalShares";
};

export type SubscriberPostRoundCommitted = {
  type: "subscriberPostRoundCommitted";
};

export type BoardStatementCreditorNotice = {
  type: "boardStatementCreditorNotice";
};

export type IncorporationTitle = {
  type: "incorporationTitle";
};

export type IncorporationFounders = {
  type: "incorporationFounders";
};

export type IncorporationShareCapital = {
  type: "incorporationShareCapital";
};

export type IncorporationFees = {
  type: "incorporationFees";
};

export type IncorporationArticles = {
  type: "incorporationArticles";
};

export type IncorporationBoard = {
  type: "incorporationBoard";
};

export type IncorporationAuditor = {
  type: "incorporationAuditor";
};

export type IncorporationSignedBy = {
  type: "incorporationSignedBy";
};

export type MergerPlanDate = {
  type: "mergerPlanDate";
};

export type MergerAccountingEffectiveDate = {
  type: "mergerAccountingEffectiveDate";
};

export type MergerAcquiringCompanyName = {
  type: "mergerAcquiringCompanyName";
};

export type MergerAcquiringCompanyOrgNo = {
  type: "mergerAcquiringCompanyOrgNo";
};

export type MergerAcquiringCompanyAddress = {
  type: "mergerAcquiringCompanyAddress";
};

export type MergerAcquiringCompanyCity = {
  type: "mergerAcquiringCompanyCity";
};

export type MergerTransferringCompanyName = {
  type: "mergerTransferringCompanyName";
};

export type MergerTransferringCompanyOrgNo = {
  type: "mergerTransferringCompanyOrgNo";
};

export type MergerTransferringCompanyAddress = {
  type: "mergerTransferringCompanyAddress";
};

export type MergerTransferringCompanyCity = {
  type: "mergerTransferringCompanyCity";
};

export type MergerParentName = {
  type: "mergerParentName";
};

export type MergerParentOrgNo = {
  type: "mergerParentOrgNo";
};

export type MergerFinancialInstrumentsDescription = {
  type: "mergerFinancialInstrumentsDescription";
};

export type MergerSpecialRightsDescription = {
  type: "mergerSpecialRightsDescription";
};

export type OptionsProgramTitle = {
  type: "optionsProgramTitle";
};

export type OptionsStrikePrice = {
  type: "optionsStrikePrice";
};

export type OptionsShareClass = {
  type: "optionsShareClass";
};

export type OptionsNumberOfOptions = {
  type: "optionsNumberOfOptions";
};

export type OptionsGrantDate = {
  type: "optionsGrantDate";
};

export type OptionsExpirationDate = {
  type: "optionsExpirationDate";
};

export type OptionsRemarks = {
  type: "optionsRemarks";
};

export type OptionsVestingType = {
  type: "optionsVestingType";
};

export type OptionsVestingFrequency = {
  type: "optionsVestingFrequency";
};

export type OptionsVestingDuration = {
  type: "optionsVestingDuration";
};

export type OptionsVestingCliff = {
  type: "optionsVestingCliff";
};

export type DataRequest =
  //companyinformation
  | CompanyName
  | CompanyOrgNo
  | TaxAssessmentPerShareCompanyInformation
  | NominalValuePerShare
  | FairValuePerShare
  | TotalShareCapital
  | TotalNumberOfShares
  | NumberOfShareholders

  //capital increase
  | ShareCapitalIncreaseMethod
  | ShareCapitalIncreaseTotalAmount
  | ShareCapitalIncreaseSubscriptionPrice
  | ShareCapitalIncreaseTotalNewShares
  | ShareCapitalIncreaseShareClasses
  | ShareCapitalIncreaseTotalContribution
  | ShareCapitalIncreasePostRoundCommitted
  | ShareCapitalIncreaseSummary
  | ContributionsInKindSummary
  | ConversionOfDebtSummary
  | ConversionOfDebtBoardStatement
  | ContributionsInKindBoardStatement
  | ConversionOfDebtSetOffDeclarationStatement
  | ConversionOfDebtSetOffDeclarationSummary
  | BoardStatementPursuantSection10_2

  // recipient
  | RecipientName

  //shareholding
  | ShareholderName
  | ShareholderTotalNumberOfShares
  | ShareholderTotalCostPrice
  | ShareholderLockUpEndDate
  | ShareholderLoanAmount
  | ShareholderCommittedTotal
  | ShareholderCommittedDrawn
  | ShareholderCommittedDistributed
  | ShareholderCommittedRemaining
  | ShareholderNumberOfPledgedShares
  | ShareholderNetAssetValuePerShare
  | ShareholderTotalNetAssetValue
  | ShareholderShareClassesOwned
  | ShareholderTotalNumberOfSharesPerClass
  | ShareholderNetAssetValuePerShareClass
  | ShareholderBic
  | ShareholderIban

  //subscription
  | SubscriberTotalAmount
  | SubscriberTotalNewShares
  | SubscriberPostRoundTotalShares
  | SubscriberPostRoundCommitted

  // Reduction of share capital
  | BoardStatementCreditorNotice

  // Incorporation
  | IncorporationTitle
  | IncorporationFounders
  | IncorporationShareCapital
  | IncorporationFees
  | IncorporationArticles
  | IncorporationBoard
  | IncorporationAuditor
  | IncorporationSignedBy

  // Merger
  | MergerPlanDate
  | MergerAccountingEffectiveDate
  | MergerAcquiringCompanyName
  | MergerAcquiringCompanyOrgNo
  | MergerAcquiringCompanyAddress
  | MergerAcquiringCompanyCity
  | MergerTransferringCompanyName
  | MergerTransferringCompanyOrgNo
  | MergerTransferringCompanyAddress
  | MergerTransferringCompanyCity
  | MergerParentName
  | MergerParentOrgNo
  | MergerFinancialInstrumentsDescription
  | MergerSpecialRightsDescription

  // Options
  | OptionsProgramTitle
  | OptionsStrikePrice
  | OptionsShareClass
  | OptionsNumberOfOptions
  | OptionsGrantDate
  | OptionsExpirationDate
  | OptionsRemarks
  | OptionsVestingType
  | OptionsVestingFrequency
  | OptionsVestingDuration
  | OptionsVestingCliff;

export type DataRequestType = DataRequest["type"];

export type DataRequestWithDate = Extract<DataRequest, { perDate?: string }>;

const dataRequestTypesWithDate: Record<DataRequestWithDate["type"], boolean> = {
  companyName: true,
  fairValuePerShare: true,
  nominalValuePerShare: true,
  numberOfShareholders: true,
  shareholderCommittedDistributed: true,
  shareholderCommittedDrawn: true,
  shareholderCommittedRemaining: true,
  shareholderCommittedTotal: true,
  shareholderNetAssetValuePerShare: true,
  shareholderShareClassesOwned: true,
  shareholderTotalCostPrice: true,
  shareholderTotalNetAssetValue: true,
  shareholderTotalNumberOfShares: true,
  shareholderTotalNumberOfSharesPerClass: true,
  shareholderNetAssetValuePerShareClass: true,
  taxAssessmentPerShare: true,
  totalNumberOfShares: true,
  totalShareCapital: true,
};

export const isDataRequestWithDate = (
  dataRequest: DataRequest,
): dataRequest is DataRequestWithDate =>
  dataRequestTypesWithDate[dataRequest.type as DataRequestWithDate["type"]];

export const isDataRequestTypeWithDate = (
  type: DataRequestType,
): type is DataRequestWithDate["type"] =>
  dataRequestTypesWithDate[type as DataRequestWithDate["type"]];

export const autotextRegex = /\{\{([\w-]+)[^}]*}}/;

export function markdownToDataRequest(text: string): DataRequest {
  const [type, ...parts] = trim(text, "{}").split(";");
  const params = Object.fromEntries(parts.map((p) => p.split("=")));
  return {
    type,
    ...params,
  } as DataRequest;
}

export function dataRequestToMarkdown(dataRequest: DataRequest): string {
  const name = dataRequest.type;
  const params = Object.entries(omit(dataRequest, "type"))
    .filter(([, v]) => v)
    .map(([k, v]) => `${k}=${v}`)
    .join(";");

  return `{{${compact([name, params]).join(";")}}}`;
}

export const initDataRequest = (type: DataRequestType): DataRequest => {
  if (isDataRequestTypeWithDate(type)) {
    return { type, perDate: toLocalDateISOString(today()) };
  }
  return { type } as DataRequest;
};
